import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Guitar,
  Music2,
  Zap,
  SlidersHorizontal,
  AudioWaveform,
  BarChart3,
  type LucideProps,
} from 'lucide-react';
import { type ComponentType } from 'react';
import { AnalysisResult, sendAIChatMessage } from '../lib/api';
import { Section } from '../utils/sectionClassifier';
import { KNKTLogo } from './KNKTLogo';
import { KnktBackgroundOrbs } from './ui/knkt-background';
import { DottedSurface } from './DottedSurface';
import ReactMarkdown from 'react-markdown';
import './AIChat.css';

const DUST_INDICES = Array.from({ length: 12 }, (_, i) => i);

interface AIChatProps {
  analysis: AnalysisResult;
  sections: Section[];
  trackInfo?: {
    trackName: string;
    artist: string;
    artists: string[];
  };
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function AIChat({ analysis, sections, trackInfo }: AIChatProps) {
  const buildWelcome = (): Message => ({
    role: 'assistant',
    content: `I've analyzed your track! It's ${analysis.tempo?.toFixed(1) || 'unknown'} BPM in the key of ${analysis.key || 'unknown'}, with ${sections.length} distinct sections. What would you like to know more about?`,
    timestamp: new Date(),
  });

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([buildWelcome()]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-clear when the analysis changes (new track uploaded). Track-specific
  // chat context shouldn't bleed across uploads.
  useEffect(() => {
    setMessages([buildWelcome()]);
    setInput('');
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis.id]);

  const requestClearChat = () => setConfirmClear(true);

  const handleClearChat = () => {
    setMessages([buildWelcome()]);
    setInput('');
    setConfirmClear(false);
  };

  // Esc cancels, Enter confirms when the dialog is open.
  useEffect(() => {
    if (!confirmClear) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmClear(false);
      if (e.key === 'Enter')  handleClearChat();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmClear]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsLoading(true);

    try {
      // Call OpenAI API
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
      
      const result = await sendAIChatMessage({
        message: userInput,
        analysis: {
          ...analysis,
          // Add track info to analysis object for AI context
          trackInfo: trackInfo ? {
            trackName: trackInfo.trackName,
            artist: trackInfo.artist,
            artists: trackInfo.artists,
          } : undefined,
        },
        sections,
        conversationHistory,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to get AI response:', error);
      const assistantMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error connecting to the AI service. Please make sure OPENAI_API_KEY is configured on the server.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const chatTemplates: Array<{
    Icon: ComponentType<LucideProps>;
    title: string;
    query: string;
  }> = [
    {
      Icon: Guitar,
      title: 'What instruments are in the main drop?',
      query: 'What instruments are used in the main drop section?',
    },
    {
      Icon: Music2,
      title: 'Explain the chord progression',
      query: 'Explain the chord progression and harmonic movement',
    },
    {
      Icon: Zap,
      title: 'How does energy change?',
      query: 'How does the energy change throughout the track?',
    },
    {
      Icon: SlidersHorizontal,
      title: 'Compare verse and drop',
      query: 'Compare the verse and main drop sections',
    },
    {
      Icon: AudioWaveform,
      title: 'What synth is used?',
      query: 'What type of synthesizer is used in the track?',
    },
    {
      Icon: BarChart3,
      title: 'Analyze the arrangement',
      query: 'Analyze the track arrangement and structure',
    },
  ];

  const handleTemplateClick = async (query: string) => {
    const userMessage: Message = {
      role: 'user',
      content: query,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
      
      const result = await sendAIChatMessage({
        message: query,
        analysis: {
          ...analysis,
          // Add track info to analysis object for AI context
          trackInfo: trackInfo ? {
            trackName: trackInfo.trackName,
            artist: trackInfo.artist,
            artists: trackInfo.artists,
          } : undefined,
        },
        sections,
        conversationHistory,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to get AI response:', error);
      const assistantMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error connecting to the AI service. Please make sure OPENAI_API_KEY is configured on the server.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!isOpen && (
        <motion.button
          className="ai-chat-toggle ai-float-btn"
          onClick={() => setIsOpen(true)}
          title="KNKT AI Music Analyst"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          aria-label="Open KNKT AI chat"
        >
          <span className="ai-float-aura" aria-hidden="true" />
          <span className="ai-float-dust" aria-hidden="true">
            {DUST_INDICES.map(i => (
              <span key={i} className={`ai-dust ai-dust--${i}`} />
            ))}
          </span>
          <span className="ai-float-logo">
            <KNKTLogo
              size={84}
              starCount={320}
              shape="circle"
              bg="#04040e"
              sunColor="#ffffff"
              starColor1="#00ffb2"
              starColor2="#00d9ff"
              spinSpeed={0.004}
              pulseMode="heartbeat"
              bpm={120}
            />
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="ai-chat-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: [0.4, 0, 0.2, 1], duration: 0.32 }}
          >
            {/* Backdrop — KNKT orbs + noise + grid as the base, with the
                3D dotted-surface wave-grid layered on top for depth. */}
            <div className="ai-chat-bg" aria-hidden="true">
              <KnktBackgroundOrbs />
              <div className="knkt-noise-overlay" />
              <div className="knkt-grid-overlay" />
              <div className="ai-chat-bg-dots">
                <DottedSurface darkMode />
              </div>
            </div>

            <div className="ai-chat-header">
              <div className="ai-header-top">
                <div className="ai-header-left">
                  <div className="ai-logo-icon">
                    <KNKTLogo
                      size={36}
                      starCount={240}
                      shape="none"
                      sunColor="#ffffff"
                      starColor1="#00ffb2"
                      starColor2="#00d9ff"
                      spinSpeed={0.005}
                      pulseMode="heartbeat"
                      bpm={120}
                    />
                  </div>
                  <div>
                    <div className="ai-title">KNKT AI</div>
                    <div className="ai-subtitle">
                      <span className="ai-status-dot" />
                      Music Intelligence Active
                    </div>
                  </div>
                </div>
                <div className="ai-header-actions">
                  {messages.length > 1 && (
                    <button
                      className="ai-iconbtn"
                      onClick={requestClearChat}
                      title="Start a fresh chat"
                      aria-label="Clear chat"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10" />
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                      </svg>
                    </button>
                  )}
                  <button className="ai-close" onClick={() => setIsOpen(false)} aria-label="Close chat">×</button>
                </div>
              </div>
            </div>

            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`message ${msg.role}`}>
                  {msg.role === 'assistant' && (
                    <div className="message-avatar" aria-hidden="true">
                      <KNKTLogo
                        size={28}
                        starCount={180}
                        shape="none"
                        sunColor="#ffffff"
                        starColor1="#00ffb2"
                        starColor2="#00d9ff"
                        spinSpeed={0.005}
                        pulseMode="heartbeat"
                        bpm={120}
                      />
                    </div>
                  )}
                  <div className="message-content">
                    {msg.role === 'assistant'
                      ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                      : msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message assistant">
                  <div className="message-avatar" aria-hidden="true">
                    <KNKTLogo
                      size={28}
                      starCount={180}
                      shape="none"
                      sunColor="#ffffff"
                      starColor1="#00ffb2"
                      starColor2="#00d9ff"
                      spinSpeed={0.005}
                      pulseMode="heartbeat"
                      bpm={120}
                      isLoading
                    />
                  </div>
                  <div className="message-content typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="ai-templates">
              {chatTemplates.map(({ Icon, title, query }, idx) => {
                const tone = ['cyan', 'mint', 'purple', 'pink'][idx % 4];
                return (
                  <button
                    type="button"
                    key={idx}
                    className={`template-card template-card--${tone}`}
                    onClick={() => handleTemplateClick(query)}
                  >
                    <span className="template-icon">
                      <Icon size={14} strokeWidth={1.8} />
                    </span>
                    <span className="template-title">{title}</span>
                  </button>
                );
              })}
            </div>

            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder="Ask about your track…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                aria-label="Send"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </div>

            {confirmClear && (
              <div
                className="ai-confirm-overlay"
                onClick={() => setConfirmClear(false)}
                role="presentation"
              >
                <div
                  className="ai-confirm-dialog"
                  role="alertdialog"
                  aria-labelledby="ai-confirm-title"
                  aria-describedby="ai-confirm-message"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="ai-confirm-ring" aria-hidden="true" />
                  <div className="ai-confirm-eyebrow">CONFIRM ACTION</div>
                  <div id="ai-confirm-title" className="ai-confirm-title">
                    Start a fresh chat?
                  </div>
                  <div id="ai-confirm-message" className="ai-confirm-message">
                    This clears the current conversation. KNKT AI won't
                    remember anything from this thread once you do.
                  </div>
                  <div className="ai-confirm-actions">
                    <button
                      type="button"
                      className="ai-confirm-btn ai-confirm-btn--ghost"
                      onClick={() => setConfirmClear(false)}
                    >
                      Keep it
                    </button>
                    <button
                      type="button"
                      className="ai-confirm-btn ai-confirm-btn--primary"
                      onClick={handleClearChat}
                      autoFocus
                    >
                      Clear chat
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Fallback function removed - now using OpenAI API
